import { useNavigate, useOutletContext, useParams } from 'react-router-dom'

import { Box } from '@factorialco/deprecated-design-system'
import {
  TrainingClassDetailIndexPageFragment,
  TrainingsDevFeatureFlagDocument,
} from 'generated/resources/graphql'

import Loading from 'modules/core/components/Loading'
import Modal from 'modules/core/components/Modal'
import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import { useQuery } from 'modules/core/hooks/useQuery'
import { track, trackingEvents } from 'modules/core/lib/Tracking'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

import { EventBox } from 'modules/trainings/components/Revamp/NewClassWizard/EventDataStep/EventBox'
import { SessionData } from 'modules/trainings/components/Revamp/NewClassWizard/hooks/useNewClassWizardSteps'
import { formatDueDate } from 'modules/trainings/components/Revamp/TrainingsDetail/Classes/EditClass/ClassEvents'
import { useCreateSessionRelatorioUnicoField } from 'modules/trainings/hooks/SessionRelatorioUnicoFields/useCreateSessionRelatorioUnicoField'
import { useCreateSession } from 'modules/trainings/hooks/Sessions/useCreateSession'
import { useIsRelatorioEnabled } from 'modules/trainings/hooks/Settings/settingUtils'

const NewSessionModal = () => {
  const navigate = useNavigate()
  const { navigation } = useNavigation()
  const { classId, id } = useParams()
  const { currentClass } = useOutletContext<{
    currentClass: TrainingClassDetailIndexPageFragment
  }>()
  const { company } = useCurrentSession()
  const { create } = useCreateSession({
    onCompleted(data) {
      if (data.trainingsMutations.createTrainingsSession?.errors.length) {
        return
      }
    },
  })
  const { data: featuresData, loading: loadingFeatures } = useQuery(
    TrainingsDevFeatureFlagDocument,
    {
      variables: {
        companyId: company.id,
      },
    }
  )
  const devTrainings = featuresData?.features.featuresConnection.nodes.find(
    (n) => n.name === 'dev_trainings'
  )

  const { create: createRU } = useCreateSessionRelatorioUnicoField()
  const companyId = useCurrentSession().company.id
  const relatorioEnabled = useIsRelatorioEnabled(companyId)

  const onClose = () =>
    navigate(
      navigation.trainings.list.details_revamp.view.trainingClasses.detail.sessions.fullPath({
        id: Number(id),
        classId: Number(classId),
      })
    )
  const onSave = async (sessionData: SessionData) => {
    const dueDate = sessionData.dueDate ? formatDueDate(sessionData.dueDate) : ''
    const sessionInstructors = {
      employeeIds: sessionData.sessionInstructors.employeeIds ?? [],
      sessionId: sessionData.sessionInstructors.sessionId ?? undefined,
      companyId: company.id,
    }

    const response = await create({
      ...sessionData,
      trainingId: Number(currentClass.training?.id),
      trainingClassId: Number(currentClass.id),
      subsidized: sessionData.subsidized ?? false,
      reminders: sessionData.reminders,
      dueDate: dueDate,
      recurrent: sessionData.recurrent,
      sessionInstructors: sessionInstructors,
    })

    const handled = handleResult(response, () => {
      app.setFlash({
        type: 'wrong',
        message: i18n.t('trainings.trainings_revamp.classes_sidepanel.events.error_create'),
      })
    })

    if (handled.success) {
      const sessionId = handled.data?.session?.id
      app.setFlash({
        type: 'correct',
        message: i18n.t('trainings.trainings_revamp.classes_sidepanel.events.success_create'),
      })

      if (relatorioEnabled && sessionId) {
        if (sessionData.sessionRelatorioUnicoField) {
          const response = await createRU({
            sessionId: sessionId,
            trainingSchedule: sessionData.sessionRelatorioUnicoField?.trainingSchedule,
          })
          const handled = handleResult(response, () => {
            app.setFlash({
              type: 'wrong',
              message: i18n.t('trainings.training_relatorio_fields.fields_created_error'),
            })
          })
          if (handled.success) {
            app.setFlash({
              type: 'correct',
              message: i18n.t('trainings.training_relatorio_fields.fields_created'),
            })
          }
        }
      }
    }
    onClose()
    return handled
  }

  if (loadingFeatures) return <Loading />

  return (
    <Modal
      title={i18n.t('trainings.trainings_revamp.sessions.new_session_modal.title')}
      illustration="document-edit"
      onClose={onClose}
      onSubmit={() => {
        track(trackingEvents.training.trainingSession.saveNewSession)
        navigate(
          navigation.trainings.list.details_revamp.view.trainingClasses.detail.sessions.fullPath({
            id: Number(id),
            classId: Number(classId),
          })
        )
      }}
      disableFocusTrap
    >
      <Box width="s600">
        <EventBox
          trainingId={Number(id)}
          trainingClassId={Number(currentClass.id)}
          border={false}
          onClose={onClose}
          onSave={onSave}
          groupStart={currentClass.startDate ?? ''}
          groupEnd={currentClass.endDate ?? ''}
          featureFlag={!!devTrainings}
        />
      </Box>
    </Modal>
  )
}
export default NewSessionModal
